import { useState, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Autocomplete } from '@/components/common/inputs/autocomplete';
import {
  CircleNotchIcon,
  UploadIcon,
  XIcon,
  FileTextIcon,
} from '@phosphor-icons/react';
import { useCreateDiscount } from '@/api/rpc/hooks/accounts-receivable/useCreateDiscount';
import { useAvailableCategories } from '@/api/rpc/hooks/accounts-receivable/useDiscounts';
import { useProductDiscountGroups } from '@/api/rpc/hooks/accounts-receivable/useProductDiscountGroups';
import useCustomers from '@/api/accounts-receivable/customers/useCustomers';
import useProductsV2 from '@/api/inventory-management/products/useProductsV2';
import type {
  CreateDiscountInput,
  DiscountType,
  DiscountTargetType,
} from '@erp/rpc';

const ALLOWED_FILE_TYPES = [
  '.pdf',
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.webp',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.csv',
];

const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
];

interface CreateDiscountFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const DISCOUNT_TYPE_OPTIONS: { value: DiscountType; label: string }[] = [
  { value: 'FIXED_PRICE', label: 'Fixed Price' },
  { value: 'PERCENT', label: 'Percentage Off' },
];

type CustomerScope = 'all' | 'specific';

export function CreateDiscountFormModal({
  open,
  onOpenChange,
}: CreateDiscountFormModalProps) {
  const createDiscount = useCreateDiscount();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Customer scope state
  const [customerScope, setCustomerScope] = useState<CustomerScope>('specific');
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(
    null,
  );
  const [customerSearch, setCustomerSearch] = useState('');

  // Target type state
  const [targetType, setTargetType] = useState<DiscountTargetType>('SKU');
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null,
  );
  const [productSearch, setProductSearch] = useState('');

  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [categorySearch, setCategorySearch] = useState('');

  const [selectedProductLine, setSelectedProductLine] = useState<string>('');
  const [productLineSearch, setProductLineSearch] = useState('');

  const [selectedDiscountGroupId, setSelectedDiscountGroupId] = useState<
    string | null
  >(null);

  // Discount details state
  const [name, setName] = useState<string>('');
  const [discountType, setDiscountType] = useState<DiscountType>('FIXED_PRICE');
  const [value, setValue] = useState<string>('');
  const [effectiveStart, setEffectiveStart] = useState<string>('');
  const [effectiveEnd, setEffectiveEnd] = useState<string>('');

  // End customer state (for contingent discounts)
  const [endCustomerName, setEndCustomerName] = useState<string>('');

  // Attachment state
  const [attachmentUrl, setAttachmentUrl] = useState<string | null>(null);
  const [attachmentName, setAttachmentName] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Fetch data for dropdowns - always enabled when modal is open
  const { data: customersData } = useCustomers(
    { page: 1, pageSize: 50, search: customerSearch || undefined },
    open,
  );
  const { data: productsData } = useProductsV2(
    { page: 1, pageSize: 50, search: productSearch || undefined },
    open,
  );
  const { categories, productLines } = useAvailableCategories();
  const { groups: discountGroups } = useProductDiscountGroups();

  const customers = useMemo(
    () => customersData?.edges ?? [],
    [customersData?.edges],
  );
  const products = useMemo(
    () => productsData?.edges ?? [],
    [productsData?.edges],
  );

  // Filter categories and product lines based on search
  const filteredCategories = useMemo(() => {
    if (!categorySearch) return categories;
    const searchLower = categorySearch.toLowerCase();
    return categories.filter((c) => c.toLowerCase().includes(searchLower));
  }, [categories, categorySearch]);

  const filteredProductLines = useMemo(() => {
    if (!productLineSearch) return productLines;
    const searchLower = productLineSearch.toLowerCase();
    return productLines.filter((p) => p.toLowerCase().includes(searchLower));
  }, [productLines, productLineSearch]);

  // Validation
  const numericValue = parseFloat(value);
  const isValueValid = !isNaN(numericValue) && numericValue > 0;

  const isTargetValid = useMemo(() => {
    switch (targetType) {
      case 'SKU':
        return !!selectedProductId;
      case 'PRODUCT_CATEGORY':
        return !!selectedCategory;
      case 'PRODUCT_LINE':
        return !!selectedProductLine;
      case 'PRODUCT_DISCOUNT_GROUP':
        return !!selectedDiscountGroupId;
      case 'ALL_PRODUCTS':
        return true;
      default:
        return false;
    }
  }, [
    targetType,
    selectedProductId,
    selectedCategory,
    selectedProductLine,
    selectedDiscountGroupId,
  ]);

  const isCustomerValid =
    customerScope === 'all' ||
    (customerScope === 'specific' && !!selectedCustomerId);

  const isFormValid =
    name.trim().length > 0 &&
    isValueValid &&
    isTargetValid &&
    isCustomerValid &&
    !isUploading;

  function resetForm() {
    setCustomerScope('specific');
    setSelectedCustomerId(null);
    setCustomerSearch('');
    setTargetType('SKU');
    setSelectedProductId(null);
    setProductSearch('');
    setSelectedCategory('');
    setCategorySearch('');
    setSelectedProductLine('');
    setProductLineSearch('');
    setSelectedDiscountGroupId(null);
    setName('');
    setDiscountType('FIXED_PRICE');
    setValue('');
    setEffectiveStart('');
    setEffectiveEnd('');
    setEndCustomerName('');
    setAttachmentUrl(null);
    setAttachmentName(null);
    setUploadError(null);
  }

  async function handleFileSelect(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      setUploadError(
        `Invalid file type. Allowed: ${ALLOWED_FILE_TYPES.join(', ')}`,
      );
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadError('File size must be less than 10MB');
      return;
    }

    setUploadError(null);
    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/uploads/file?folder=discounts', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload file');
      }

      const result = await response.json();
      setAttachmentUrl(result.url);
      setAttachmentName(file.name);
    } catch (error) {
      setUploadError(
        error instanceof Error ? error.message : 'Failed to upload file',
      );
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }

  function handleRemoveAttachment() {
    setAttachmentUrl(null);
    setAttachmentName(null);
    setUploadError(null);
  }

  async function handleSubmit() {
    if (!isFormValid) return;

    const customerId = customerScope === 'all' ? null : selectedCustomerId;
    // Only include end customer name if a specific customer is selected
    const endCustomer =
      customerScope === 'specific' && endCustomerName.trim()
        ? endCustomerName.trim()
        : null;

    // Build the scope based on target type
    let scope: CreateDiscountInput['scopes'][0];

    switch (targetType) {
      case 'SKU':
        scope = {
          targetType: 'SKU',
          customerId,
          targetId: selectedProductId!,
          attachment: attachmentUrl,
          endCustomerName: endCustomer,
        };
        break;
      case 'PRODUCT_CATEGORY':
        scope = {
          targetType: 'PRODUCT_CATEGORY',
          customerId,
          targetValue: selectedCategory,
          attachment: attachmentUrl,
          endCustomerName: endCustomer,
        };
        break;
      case 'PRODUCT_LINE':
        scope = {
          targetType: 'PRODUCT_LINE',
          customerId,
          targetValue: selectedProductLine,
          attachment: attachmentUrl,
          endCustomerName: endCustomer,
        };
        break;
      case 'PRODUCT_DISCOUNT_GROUP':
        scope = {
          targetType: 'PRODUCT_DISCOUNT_GROUP',
          customerId,
          targetId: selectedDiscountGroupId!,
          attachment: attachmentUrl,
          endCustomerName: endCustomer,
        };
        break;
      case 'ALL_PRODUCTS':
        scope = {
          targetType: 'ALL_PRODUCTS',
          customerId,
          attachment: attachmentUrl,
          endCustomerName: endCustomer,
        };
        break;
    }

    const input: CreateDiscountInput = {
      name: name.trim(),
      discountType,
      value: numericValue,
      effectiveStart: effectiveStart || null,
      effectiveEnd: effectiveEnd || null,
      scopes: [scope],
    };

    try {
      await createDiscount.mutateAsync(input);
      resetForm();
      onOpenChange(false);
    } catch {
      // Error is handled by the hook's onError callback
    }
  }

  function getValueLabel(): string {
    switch (discountType) {
      case 'FIXED_PRICE':
        return 'Price ($)';
      case 'PERCENT':
        return 'Discount (%)';
      default:
        return 'Value';
    }
  }

  function getValueHelperText(): string {
    switch (discountType) {
      case 'FIXED_PRICE':
        return 'The fixed price customers will pay';
      case 'PERCENT':
        return 'Percentage off list price (0-100)';
      default:
        return '';
    }
  }

  // Get selected customer name for display
  const selectedCustomerName = useMemo(() => {
    const customer = customers.find((c) => c.id === selectedCustomerId);
    return customer?.name ?? '';
  }, [customers, selectedCustomerId]);

  // Get selected product name for display
  const selectedProductDisplay = useMemo(() => {
    const product = products.find((p) => p.id === selectedProductId);
    if (!product) return '';
    return product.baseSku
      ? `${product.baseSku} - ${product.name}`
      : product.name;
  }, [products, selectedProductId]);

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          resetForm();
        }
        onOpenChange(isOpen);
      }}
    >
      <DialogContent
        className="sm:max-w-lg max-h-[90vh] overflow-y-auto"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Create Discount</DialogTitle>
          <DialogDescription>
            Create a new discount rule for customers and products
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-4">
          {/* Section: Customer Scope */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">
              Customer Scope
            </h3>
            <RadioGroup
              value={customerScope}
              onValueChange={(val) => {
                setCustomerScope(val as CustomerScope);
                if (val === 'all') {
                  setSelectedCustomerId(null);
                }
              }}
              className="flex gap-6"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="all" id="all-customers" />
                <Label htmlFor="all-customers" className="font-normal text-sm">
                  All Customers
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="specific" id="specific-customer" />
                <Label
                  htmlFor="specific-customer"
                  className="font-normal text-sm"
                >
                  Specific Customer
                </Label>
              </div>
            </RadioGroup>

            {customerScope === 'specific' && (
              <Autocomplete
                multiSelect={false}
                options={customers}
                searchValue={customerSearch}
                onSearchValueChange={setCustomerSearch}
                getValue={(customer) => customer.id}
                getLabel={(customer) => customer.name}
                value={selectedCustomerId ?? undefined}
                onValueChange={(val) => setSelectedCustomerId(val ?? null)}
                placeholder="Search customers..."
                searchPlaceholder="Type to search..."
                emptyMessage="No customers found"
                className="w-full"
              />
            )}

            {/* End Customer Name - only show when specific customer is selected */}
            {customerScope === 'specific' && (
              <div className="space-y-1.5">
                <Label
                  htmlFor="end-customer-name"
                  className="text-xs text-muted-foreground"
                >
                  End Customer (Optional)
                </Label>
                <Input
                  id="end-customer-name"
                  value={endCustomerName}
                  onChange={(e) => setEndCustomerName(e.target.value)}
                  placeholder="e.g., Nestle, Coca-Cola"
                />
                <p className="text-xs text-muted-foreground">
                  If set, this discount requires verification when matched on a
                  PO
                </p>
              </div>
            )}
          </div>

          {/* Section: Product Target */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">
              Product Target
            </h3>
            <Select
              value={targetType}
              onValueChange={(val) => {
                setTargetType(val as DiscountTargetType);
                // Reset target-specific selections
                setSelectedProductId(null);
                setProductSearch('');
                setSelectedCategory('');
                setCategorySearch('');
                setSelectedProductLine('');
                setProductLineSearch('');
                setSelectedDiscountGroupId(null);
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select target type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="SKU">Specific SKU</SelectItem>
                <SelectItem value="PRODUCT_CATEGORY">
                  Product Category
                </SelectItem>
                <SelectItem value="PRODUCT_LINE">Product Line</SelectItem>
                <SelectItem value="PRODUCT_DISCOUNT_GROUP">
                  Discount Group
                </SelectItem>
                <SelectItem value="ALL_PRODUCTS">All Products</SelectItem>
              </SelectContent>
            </Select>

            {/* Target-specific fields */}
            {targetType === 'SKU' && (
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">
                  Select Product
                </Label>
                <Autocomplete
                  multiSelect={false}
                  options={products}
                  searchValue={productSearch}
                  onSearchValueChange={setProductSearch}
                  getValue={(product) => product.id}
                  getLabel={(product) =>
                    product.baseSku
                      ? `${product.baseSku} - ${product.name}`
                      : product.name
                  }
                  value={selectedProductId ?? undefined}
                  onValueChange={(val) => setSelectedProductId(val ?? null)}
                  placeholder="Search products..."
                  searchPlaceholder="Type to search..."
                  emptyMessage="No products found"
                  className="w-full"
                />
              </div>
            )}

            {targetType === 'PRODUCT_CATEGORY' && (
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">
                  Select Category
                </Label>
                <Autocomplete
                  multiSelect={false}
                  options={filteredCategories.map((c) => ({ id: c, name: c }))}
                  searchValue={categorySearch}
                  onSearchValueChange={setCategorySearch}
                  getValue={(opt) => opt.id}
                  getLabel={(opt) => opt.name}
                  value={selectedCategory || undefined}
                  onValueChange={(val) => setSelectedCategory(val ?? '')}
                  placeholder="Search categories..."
                  searchPlaceholder="Type to search..."
                  emptyMessage="No categories found"
                  className="w-full"
                />
              </div>
            )}

            {targetType === 'PRODUCT_LINE' && (
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">
                  Select Product Line
                </Label>
                <Autocomplete
                  multiSelect={false}
                  options={filteredProductLines.map((p) => ({
                    id: p,
                    name: p,
                  }))}
                  searchValue={productLineSearch}
                  onSearchValueChange={setProductLineSearch}
                  getValue={(opt) => opt.id}
                  getLabel={(opt) => opt.name}
                  value={selectedProductLine || undefined}
                  onValueChange={(val) => setSelectedProductLine(val ?? '')}
                  placeholder="Search product lines..."
                  searchPlaceholder="Type to search..."
                  emptyMessage="No product lines found"
                  className="w-full"
                />
              </div>
            )}

            {targetType === 'PRODUCT_DISCOUNT_GROUP' && (
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">
                  Select Discount Group
                </Label>
                <Select
                  value={selectedDiscountGroupId ?? ''}
                  onValueChange={(val) =>
                    setSelectedDiscountGroupId(val || null)
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select discount group..." />
                  </SelectTrigger>
                  <SelectContent>
                    {discountGroups.length === 0 ? (
                      <SelectItem value="" disabled>
                        No discount groups available
                      </SelectItem>
                    ) : (
                      discountGroups.map((group) => (
                        <SelectItem key={group.id} value={group.id}>
                          {group.name} ({group.itemCount} products)
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            {targetType === 'ALL_PRODUCTS' && (
              <p className="text-xs text-muted-foreground bg-muted/50 px-3 py-2 rounded-md">
                This discount will apply to all products.
              </p>
            )}
          </div>

          {/* Section: Discount Details */}
          <div className="space-y-4 border-t pt-5">
            <h3 className="text-sm font-semibold text-foreground">
              Discount Details
            </h3>

            <div className="grid gap-4">
              {/* Discount Name */}
              <div className="space-y-1.5">
                <Label htmlFor="discount-name" className="text-sm">
                  Discount Name
                </Label>
                <Input
                  id="discount-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter a descriptive name"
                />
              </div>

              {/* Discount Type and Value - side by side */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="discount-type" className="text-sm">
                    Discount Type
                  </Label>
                  <Select
                    value={discountType}
                    onValueChange={(val) =>
                      setDiscountType(val as DiscountType)
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {DISCOUNT_TYPE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="discount-value" className="text-sm">
                    {getValueLabel()}
                  </Label>
                  <Input
                    id="discount-value"
                    type="number"
                    step="0.01"
                    min="0"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder="0.00"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground -mt-2">
                {getValueHelperText()}
              </p>

              {/* Effective Dates */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="effective-start" className="text-sm">
                    Effective Start
                  </Label>
                  <Input
                    id="effective-start"
                    type="date"
                    value={effectiveStart}
                    onChange={(e) => setEffectiveStart(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="effective-end" className="text-sm">
                    Effective End
                  </Label>
                  <Input
                    id="effective-end"
                    type="date"
                    value={effectiveEnd}
                    onChange={(e) => setEffectiveEnd(e.target.value)}
                  />
                </div>
              </div>

              {/* Attachment Upload */}
              <div className="space-y-1.5">
                <Label className="text-sm">Attachment</Label>
                {attachmentUrl && attachmentName ? (
                  <div className="flex items-center gap-2 p-2 rounded-md bg-muted/50 border">
                    <FileTextIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="text-sm flex-1 truncate">
                      {attachmentName}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={handleRemoveAttachment}
                    >
                      <XIcon className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={ALLOWED_FILE_TYPES.join(',')}
                      onChange={handleFileSelect}
                      className="hidden"
                      id="attachment-upload"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                    >
                      {isUploading ? (
                        <CircleNotchIcon className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <UploadIcon className="h-4 w-4 mr-2" />
                      )}
                      {isUploading ? 'Uploading...' : 'Upload File'}
                    </Button>
                  </div>
                )}
                {uploadError && (
                  <p className="text-xs text-destructive">{uploadError}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  PDF, images, Word, Excel, CSV (max 10MB)
                </p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={createDiscount.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid || createDiscount.isPending}
          >
            {createDiscount.isPending && (
              <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
            )}
            Create Discount
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
